/**
 * Clay mega menu — sidebar tab / panel swap for rich desktop mega menus.
 */
const CLAY_MEGA_EDITOR_KEY = 'clayMegaEditorSelection';

function isClayMegaDesignMode() {
  return Boolean(window.Shopify?.designMode);
}

function readClayMegaEditorSelection() {
  if (!isClayMegaDesignMode()) return null;
  try {
    return JSON.parse(localStorage.getItem(CLAY_MEGA_EDITOR_KEY) || 'null');
  } catch (error) {
    return null;
  }
}

function saveClayMegaEditorSelection(payload) {
  if (!isClayMegaDesignMode() || !payload?.panelHandle) return;
  try {
    localStorage.setItem(CLAY_MEGA_EDITOR_KEY, JSON.stringify(payload));
  } catch (error) {
    /* editor preview storage can be unavailable */
  }
}

function findClayMegaByHandle(panelHandle, root = document) {
  if (!panelHandle || !root?.querySelector) return null;
  const handle = window.CSS?.escape ? CSS.escape(panelHandle) : panelHandle;
  return root.querySelector(
    `clay-mega-menu[data-panel-handle="${handle}"], .clay-mega[data-panel-handle="${handle}"]`
  );
}

function findClayMegaFromNode(node) {
  if (!node || !(node instanceof Element)) return null;
  if (node.matches?.('clay-mega-menu, .clay-mega')) return node;
  return node.closest?.('clay-mega-menu, .clay-mega') || node.querySelector?.('clay-mega-menu, .clay-mega');
}

function openClayMegaDetails(clay) {
  if (!clay) return;
  const details = clay.closest('details.mega-menu');
  if (!details) return;
  details.setAttribute('open', '');
  const summary = details.querySelector('summary');
  if (summary) summary.setAttribute('aria-expanded', 'true');
}

function applyClayMegaItemKey(clay, itemKey) {
  if (!clay || !itemKey) return;

  if (typeof clay.activate === 'function') {
    clay.activate(itemKey, { persist: false });
    return;
  }

  clay.querySelectorAll('[data-clay-mega-tab]').forEach((tab) => {
    const isActive = tab.dataset.itemKey === itemKey;
    tab.classList.toggle('is-active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  clay.querySelectorAll('[data-clay-mega-panel]').forEach((panel) => {
    const isActive = panel.dataset.itemKey === itemKey;
    panel.classList.toggle('is-active', isActive);
    if (isActive) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  });
}

function restoreClayMegaEditorSelection(root = document) {
  const saved = readClayMegaEditorSelection();
  if (!saved?.panelHandle) return false;

  const clay = findClayMegaByHandle(saved.panelHandle, root);
  if (!clay) return false;

  openClayMegaDetails(clay);
  applyClayMegaItemKey(clay, saved.itemKey);
  return true;
}

if (!customElements.get('clay-mega-menu')) {
  class ClayMegaMenu extends HTMLElement {
    constructor() {
      super();
      this.onTabActivate = this.onTabActivate.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onDetailsToggle = this.onDetailsToggle.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-clay-mega-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-clay-mega-panel]'));
      this.navItems = Array.from(this.querySelectorAll('[data-clay-mega-nav-item]'));
      this.footer = this.querySelector('[data-clay-mega-footer]');
      this.footerLabel = this.querySelector('[data-clay-mega-footer-label]');
      this.isCollectionSpotlight =
        this.classList.contains('clay-mega--collection-spotlight') ||
        this.classList.contains('clay-mega--collections-browse');
      this.details = this.closest('details.mega-menu');

      if (!this.tabs.length || !this.panels.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('mouseenter', this.onTabActivate);
        tab.addEventListener('focus', this.onTabActivate);
        tab.addEventListener('click', this.onTabActivate);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      if (this.details) {
        this.details.addEventListener('toggle', this.onDetailsToggle);
      }

      const saved = readClayMegaEditorSelection();
      const savedKey =
        saved?.panelHandle === this.dataset.panelHandle ? saved.itemKey : null;

      if (savedKey) {
        this.activate(savedKey, { persist: false });
        openClayMegaDetails(this);
        return;
      }

      const defaultKey = this.dataset.defaultKey;
      if (defaultKey) this.activate(defaultKey, { persist: false });
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => {
        tab.removeEventListener('mouseenter', this.onTabActivate);
        tab.removeEventListener('focus', this.onTabActivate);
        tab.removeEventListener('click', this.onTabActivate);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });
      this.details?.removeEventListener('toggle', this.onDetailsToggle);
    }

    onDetailsToggle() {
      if (!this.details?.open) return;

      if (isClayMegaDesignMode()) {
        const saved = readClayMegaEditorSelection();
        if (saved?.panelHandle === this.dataset.panelHandle && saved.itemKey) {
          this.activate(saved.itemKey, { persist: false });
        }
        return;
      }

      const defaultKey = this.dataset.defaultKey;
      if (defaultKey) this.activate(defaultKey, { persist: false });
    }

    onTabActivate(event) {
      const tab = event.currentTarget;
      const key = tab?.dataset?.itemKey;
      if (!key) return;
      this.activate(key);
    }

    onTabKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      const nextTab = this.tabs[nextIndex];
      nextTab.focus();
      this.activate(nextTab.dataset.itemKey);
    }

    activate(itemKey, options = {}) {
      if (!itemKey) return;

      const persist = options.persist !== false;
      const isOverview = itemKey === 'all';

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.itemKey === itemKey;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive || (isOverview && tab === this.tabs[0]) ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.itemKey === itemKey;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (this.isCollectionSpotlight) {
        this.classList.toggle('is-overview', isOverview);
        this.navItems.forEach((item) => {
          const match = item.dataset.itemKey === itemKey;
          item.classList.toggle('is-active', match && !isOverview);
          item.classList.add('is-expanded');
        });
        this.updateFooter(itemKey, isOverview);
      }

      if (persist) {
        saveClayMegaEditorSelection({
          panelHandle: this.dataset.panelHandle,
          itemKey,
        });
      }
    }

    updateFooter(itemKey, isOverview) {
      if (!this.footer || !this.footerLabel) return;

      if (isOverview) {
        const label = this.dataset.footerDefaultLabel || '';
        const href = this.dataset.footerDefaultHref || '';
        this.footerLabel.textContent = label;
        if (this.footer.tagName === 'A' && href) this.footer.setAttribute('href', href);
        return;
      }

      const activeTab = this.tabs.find((tab) => tab.dataset.itemKey === itemKey);
      if (!activeTab) return;

      const label = activeTab.dataset.footerLabel || this.dataset.footerDefaultLabel || '';
      const href = activeTab.dataset.footerHref || this.dataset.footerDefaultHref || '';
      this.footerLabel.textContent = label;
      if (this.footer.tagName === 'A' && href) this.footer.setAttribute('href', href);
    }

    openParentDetails() {
      openClayMegaDetails(this);
    }
  }

  customElements.define('clay-mega-menu', ClayMegaMenu);
}

/**
 * Theme editor: keep the matching clay mega menu open while selecting/editing its blocks.
 * Shopify reloads the preview iframe and then selects the first header block — ignore that.
 */
(() => {
  if (!isClayMegaDesignMode()) return;

  let ignoreWrongPanelUntil = 0;

  const markPreviewReload = () => {
    ignoreWrongPanelUntil = Date.now() + 2000;
  };

  const reassert = () => {
    restoreClayMegaEditorSelection(document);
  };

  const itemKeyFromBlock = (blockEl) => {
    const keySource =
      blockEl.closest?.('[data-clay-mega-panel], [data-clay-mega-nav-item], [data-clay-mega-tab]') ||
      blockEl.closest?.('[data-item-key]') ||
      blockEl.querySelector?.('[data-item-key]') ||
      blockEl;
    return keySource?.dataset?.itemKey;
  };

  const openClayMegaFromBlock = (blockEl, { persist = true } = {}) => {
    const clay = findClayMegaFromNode(blockEl);
    if (!clay) return false;

    const itemKey = itemKeyFromBlock(blockEl) || clay.dataset.defaultKey;
    openClayMegaDetails(clay);
    applyClayMegaItemKey(clay, itemKey);

    if (persist && clay.dataset.panelHandle) {
      saveClayMegaEditorSelection({
        panelHandle: clay.dataset.panelHandle,
        itemKey,
      });
    }

    return true;
  };

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const clay = findClayMegaFromNode(event.target);
      const saved = readClayMegaEditorSelection();
      const incomingHandle = clay?.dataset?.panelHandle;
      const fromReload = Boolean(event.detail?.load) || Date.now() < ignoreWrongPanelUntil;

      if (fromReload && saved?.panelHandle && incomingHandle && incomingHandle !== saved.panelHandle) {
        reassert();
        requestAnimationFrame(reassert);
        setTimeout(reassert, 50);
        setTimeout(reassert, 250);
        setTimeout(reassert, 700);
        return;
      }

      if (fromReload) {
        if (saved?.panelHandle) {
          reassert();
          if (clay && incomingHandle === saved.panelHandle) {
            const itemKey = itemKeyFromBlock(event.target) || saved.itemKey;
            applyClayMegaItemKey(clay, itemKey);
          }
          return;
        }
        openClayMegaFromBlock(event.target, { persist: false });
        return;
      }

      openClayMegaFromBlock(event.target, { persist: true });
    },
    true
  );

  document.addEventListener('shopify:section:unload', markPreviewReload);
  document.addEventListener('shopify:section:load', (event) => {
    markPreviewReload();
    restoreClayMegaEditorSelection(event.target || document);
    setTimeout(reassert, 50);
    setTimeout(reassert, 250);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target;
    if (!root?.querySelector?.('clay-mega-menu, .clay-mega')) return;
    if (Date.now() < ignoreWrongPanelUntil || readClayMegaEditorSelection()?.panelHandle) {
      restoreClayMegaEditorSelection(root);
    }
  });

  window.addEventListener('pageshow', () => {
    markPreviewReload();
    reassert();
  });

  markPreviewReload();
  reassert();
})();
